const normalizeKey = (value) => String(value || "").trim();
const normalizeEntityType = (value) => normalizeKey(value).toLowerCase();
const normalizeLayoutSlot = (value) => normalizeKey(value).toLowerCase();

export function dedupeNcrmPropertyLayoutKeys(items = []) {
  const seen = new Set();
  const normalized = [];
  for (const entry of Array.isArray(items) ? items : []) {
    const key = normalizeKey(entry);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    normalized.push(key);
  }
  return normalized;
}

export function buildNcrmPropertyLayoutEndpoint(entityType, slot) {
  const normalizedEntityType = normalizeEntityType(entityType);
  const normalizedSlot = normalizeLayoutSlot(slot);
  if (!normalizedEntityType || !normalizedSlot) return "";
  return `/ncrm/property-layouts/${normalizedEntityType}/${normalizedSlot}`;
}

export function resolveNcrmPropertyLayoutItems(rows = []) {
  const items = [];
  const seen = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = normalizeKey(row?.key);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    items.push({
      key,
      label: normalizeKey(row?.label) || key,
    });
  }
  return items;
}

export function buildNcrmPropertyRowMap(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    const key = normalizeKey(row?.key);
    if (!key) return acc;
    acc[key] = row;
    return acc;
  }, {});
}

export function sanitizeNcrmPropertyLayoutVisibleKeys(visibleKeys = [], availableKeys = []) {
  const availableSet = new Set(dedupeNcrmPropertyLayoutKeys(availableKeys));
  return dedupeNcrmPropertyLayoutKeys(visibleKeys).filter((key) => availableSet.has(key));
}

export function resolveNcrmPropertyLayoutState({ rows = [], payload = null, fallbackVisibleKeys = [] } = {}) {
  const items = resolveNcrmPropertyLayoutItems(rows);
  const availableFromRows = items.map((entry) => entry.key);

  const availableFromPayload = sanitizeNcrmPropertyLayoutVisibleKeys(
    payload?.available_property_keys,
    availableFromRows
  );
  const availableKeys = availableFromPayload.length ? availableFromPayload : availableFromRows;

  const visibleFromPayload = sanitizeNcrmPropertyLayoutVisibleKeys(payload?.visible_property_keys, availableKeys);
  const visibleFromFallback = sanitizeNcrmPropertyLayoutVisibleKeys(fallbackVisibleKeys, availableKeys);
  const visibleKeys = visibleFromPayload.length
    ? visibleFromPayload
    : visibleFromFallback.length
      ? visibleFromFallback
      : availableKeys.slice();

  return {
    items,
    availableKeys,
    visibleKeys,
  };
}

export function resolveNcrmOrderedPropertyRows({
  rows = [],
  rowMap = null,
  availableKeys = [],
  visibleKeys = [],
} = {}) {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const effectiveRowMap =
    rowMap && typeof rowMap === "object" && !Array.isArray(rowMap) ? rowMap : buildNcrmPropertyRowMap(normalizedRows);
  const rowKeys = dedupeNcrmPropertyLayoutKeys(
    normalizedRows.length ? normalizedRows.map((row) => row?.key) : Object.keys(effectiveRowMap)
  );
  if (!rowKeys.length) return [];

  const sanitizedAvailableKeys = sanitizeNcrmPropertyLayoutVisibleKeys(availableKeys, rowKeys);
  const effectiveAvailableKeys = sanitizedAvailableKeys.length ? sanitizedAvailableKeys : rowKeys;

  const sanitizedVisibleKeys = sanitizeNcrmPropertyLayoutVisibleKeys(visibleKeys, effectiveAvailableKeys);
  const effectiveVisibleKeys = sanitizedVisibleKeys.length ? sanitizedVisibleKeys : effectiveAvailableKeys;

  const orderedKeys = dedupeNcrmPropertyLayoutKeys([...effectiveVisibleKeys, ...effectiveAvailableKeys, ...rowKeys]);
  return orderedKeys
    .map((key) => effectiveRowMap[key])
    .filter(Boolean);
}
